import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, Shield } from 'lucide-react';
import { getLocation } from '@/lib/geo';
import { DevicePending } from '@/components/DevicePending';

export default function LoginPage() {
  const { login, api } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pending, setPending] = useState(null);
  const reason = new URLSearchParams(window.location.search).get('reason');
  const REASONS = {
    idle: 'You were logged out after a period of inactivity.',
    expired: 'Your session reached its time limit. Please log in again.',
    location: 'You were logged out because your location stopped being shared.',
  };
  const reasonText = reason ? (REASONS[reason] || reason) : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    let location = null;
    const { data: policy } = await api.get('/auth/login-policy').catch(() => ({ data: { require_location: true } }));
    if (policy.require_location) {
      try { location = await getLocation(); } catch (err) { setError(err.message); setLoading(false); return; }
    }
    const result = await login(email, password, location);
    if (!result.success) setError(result.error);
    else if (result.pending) setPending(result.pending);
    setLoading(false);
  };

  if (pending) return <DevicePending info={pending} onCancel={() => setPending(null)} />;

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1586522471429-c8eee8f0d460?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxtaW5pbWFsJTIwYXJjaGl0ZWN0dXJlJTIwd2hpdGV8ZW58MHx8fHwxNzc2MjI3NTA1fDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/65 to-slate-900/40" />
        {/* House+truck mascot — moved to bottom-RIGHT (was bottom-left)
            and slightly larger. Fully alpha-transparent (no white frame). */}
        <img
          src="/rtr-house-truck-icon.png"
          alt=""
          aria-hidden="true"
          data-testid="login-mascot-secondary-logo"
          className="absolute right-10 bottom-10 w-56 lg:w-72 opacity-95 drop-shadow-[0_8px_22px_rgba(0,0,0,0.45)] pointer-events-none select-none"
        />
        {/* Brand stack — RTR logo centred ABOVE the wordmark, both
            horizontally aligned around the same axis. Wordmark sits
            slightly higher in the visual hierarchy now. */}
        <div className="relative z-10 text-white p-12 max-w-lg flex flex-col items-center text-center">
          <h1 className="sr-only">RTR CRM — Roofs To Remedy Ltd</h1>
          <img
            src="/rtr-logo.png"
            alt="RTR"
            data-testid="login-rtr-logo"
            className="block h-20 lg:h-24 w-auto object-contain drop-shadow-[0_3px_12px_rgba(211,84,0,0.55)] mb-4"
          />
          <img
            src="/rtr-wordmark.png"
            alt="Roofs To Remedy Ltd"
            className="block h-14 lg:h-20 w-auto max-w-full mb-4 drop-shadow-[0_3px_14px_rgba(0,0,0,0.55)]"
          />
          <p className="text-slate-300/90 text-base leading-relaxed">Complete roofing business management. Clients, appraisals, quotes, and job tracking — all in one place.</p>
          <div className="mt-10 flex items-center gap-6 text-xs uppercase tracking-[0.22em] text-white/40 font-semibold">
            <span>1,800+ clients</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>4,700+ appraisals</span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F9F9F8]">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <h1 className="sr-only">RTR CRM</h1>
            <img src="/rtr-logo.png" alt="RTR" className="block h-12 w-auto object-contain" />
            <img src="/rtr-wordmark.png" alt="Roofs To Remedy Ltd" className="block h-7 w-auto object-contain" />
          </div>
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope] mb-2">Welcome back</h2>
          <p className="text-slate-500 text-sm mb-8">Sign in to your account</p>
          {reasonText && !error && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-md mb-6 text-sm" data-testid="login-session-reason">{reasonText}</div>}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6 text-sm" data-testid="login-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-[#E2E2DF] rounded-md bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]/30 focus:border-[#D35400] transition-all duration-200" placeholder="you@company.com" required />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2">Password</label>
              <div className="relative">
                <input data-testid="login-password-input" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 border border-[#E2E2DF] rounded-md bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]/30 focus:border-[#D35400] transition-all duration-200 pr-12" placeholder="Enter password" required />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full py-3 px-4 bg-[#D35400] hover:bg-[#A04000] text-white font-semibold rounded-md transition-all duration-200 disabled:opacity-50 text-sm shadow-sm hover:shadow-md hover:shadow-orange-500/20">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          <div className="mt-6 flex gap-2 text-xs text-slate-500 leading-relaxed" data-testid="login-privacy-notice">
            <Shield size={14} className="shrink-0 mt-0.5 text-slate-400" />
            <p>For security, each login is recorded with the device, network address and time. Your location is checked at login and regularly while you're logged in, and new devices need approval by an admin. Sessions end after a period without activity, after a set number of hours, and when the browser is closed.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
